import { useState } from "react";
import { Link } from "react-router-dom";
import { CheckCircle2, Loader2 } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

const LOGO = "https://customer-assets-cm19k8pv.emergentagent.net/job_school-hub-1220/artifacts/eggcq1ix_302319671_479406710859175_2268986965834108785_n.jpg";

export default function RegistroTutor() {
  const { config } = useAuth();
  const [form, setForm] = useState({
    nombre: "", email: "", password: "", telefono: "", direccion: "",
    nombre_estudiante: "", grado_estudiante: "", mensaje: "",
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [listo, setListo] = useState(false);

  const enviar = async (e) => {
    e.preventDefault();
    setError("");
    if (form.password.length < 6) return setError("La contraseña debe tener al menos 6 caracteres");
    setBusy(true);
    try {
      await api.post("/registro-tutor", form);
      setListo(true);
    } catch (err) {
      setError(apiError(err));
    }
    setBusy(false);
  };

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b border-border">
        <div className="mx-auto flex max-w-3xl items-center gap-3 px-6 py-5">
          <img src={LOGO} alt="Colegio Chiki Genios" className="h-11 w-auto" />
          <div>
            <p className="font-display text-base font-extrabold tracking-tight">{config.nombre}</p>
            <p className="text-xs text-muted-foreground">
              Registro de padres y tutores · {config.anio_lectivo}
            </p>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-3xl px-6 py-12">
        {listo ? (
          <div className="rise rounded-lg border border-primary/30 bg-primary/5 p-10 text-center" data-testid="registro-exitoso">
            <CheckCircle2 className="mx-auto h-10 w-10 text-primary" />
            <h1 className="mt-5 font-display text-3xl font-extrabold tracking-tight">¡Solicitud enviada!</h1>
            <p className="mx-auto mt-4 max-w-md text-sm leading-relaxed text-muted-foreground">
              La administración del colegio revisará su solicitud. Cuando sea aprobada podrá iniciar sesión con el
              correo y la contraseña que acaba de registrar.
            </p>
            <Button asChild className="mt-8 rounded-full">
              <Link to="/login" data-testid="ir-a-login">Ir al inicio de sesión</Link>
            </Button>
          </div>
        ) : (
          <>
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground">Nuevo acceso</p>
            <h1 className="mt-3 font-display text-4xl font-extrabold tracking-tight sm:text-5xl">
              Cree su cuenta del portal de padres
            </h1>
            <p className="mt-4 max-w-xl text-sm leading-relaxed text-muted-foreground">
              Complete el formulario y la administración vinculará a su hijo o hija con su cuenta. Luego podrá ver
              calificaciones, tareas, actividades y su estado de cuenta.
            </p>

            <form onSubmit={enviar} className="mt-10 grid gap-5 sm:grid-cols-2" data-testid="form-registro">
              <div className="space-y-2 sm:col-span-2">
                <Label>Nombre completo del padre / tutor</Label>
                <Input value={form.nombre} required data-testid="registro-nombre"
                       onChange={(e) => setForm({ ...form, nombre: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label>Correo electrónico</Label>
                <Input type="email" value={form.email} required data-testid="registro-email"
                       onChange={(e) => setForm({ ...form, email: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label>Contraseña</Label>
                <Input type="password" value={form.password} required data-testid="registro-password"
                       onChange={(e) => setForm({ ...form, password: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label>Teléfono</Label>
                <Input value={form.telefono} data-testid="registro-telefono"
                       onChange={(e) => setForm({ ...form, telefono: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label>Dirección</Label>
                <Input value={form.direccion} data-testid="registro-direccion"
                       onChange={(e) => setForm({ ...form, direccion: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label>Nombre del estudiante</Label>
                <Input value={form.nombre_estudiante} data-testid="registro-estudiante"
                       onChange={(e) => setForm({ ...form, nombre_estudiante: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label>Grado al que aspira o cursa</Label>
                <Input value={form.grado_estudiante} data-testid="registro-grado"
                       onChange={(e) => setForm({ ...form, grado_estudiante: e.target.value })} />
              </div>
              <div className="space-y-2 sm:col-span-2">
                <Label>Mensaje para la administración (opcional)</Label>
                <Textarea rows={4} value={form.mensaje} data-testid="registro-mensaje"
                          onChange={(e) => setForm({ ...form, mensaje: e.target.value })} />
              </div>
              {error && (
                <p className="sm:col-span-2 rounded-md bg-destructive/10 px-4 py-3 text-sm text-destructive"
                   data-testid="registro-error">{error}</p>
              )}
              <Button type="submit" disabled={busy} className="sm:col-span-2 rounded-full py-6 text-base"
                      data-testid="enviar-registro">
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Enviar solicitud de registro"}
              </Button>
              <p className="sm:col-span-2 text-center text-xs text-muted-foreground">
                ¿Ya tiene cuenta? <Link to="/login" className="text-primary hover:underline">Inicie sesión aquí</Link>
              </p>
            </form>
          </>
        )}
      </main>
    </div>
  );
}
